import { useTheme } from "@/context/ThemeContext";

// Known bright brand/division/competition colors that need a darker,
// more readable variant on a white (light-theme) background.
const LIGHT_ACCENT_MAP = {
    "#ffd700": "#b8860b", // gold (TOP / Champions)
    "#e0e0e0": "#8a8a8a", // silver (DOP)
    "#c0c0c0": "#8a8a8a",
    "#00e5ff": "#0091a8", // cyan (HOPE)
    "#00bcd4": "#0090a8", // conference cyan
    "#e8ff00": "#8f9b00", // lime yellow
    "#7ce38b": "#1f9d4d", // light green
    "#2bb6ff": "#0090e0", // brand blue
};

// Return a light-theme-appropriate version of a hex color. Non-hex values
// (e.g. "rgb(var(--tc-xxxxxx))" tokens) are returned unchanged because the
// CSS `.light` overrides already handle those.
export function accentForLight(color) {
    if (!color) return color;
    const s = String(color).trim().toLowerCase();
    if (s in LIGHT_ACCENT_MAP) return LIGHT_ACCENT_MAP[s];
    if (/^#[0-9a-f]{6}$/.test(s)) {
        const r = parseInt(s.slice(1, 3), 16);
        const g = parseInt(s.slice(3, 5), 16);
        const b = parseInt(s.slice(5, 7), 16);
        const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        if (lum > 145) {
            const f = 120 / lum;
            const h = (v) => Math.round(v * f).toString(16).padStart(2, "0");
            return `#${h(r)}${h(g)}${h(b)}`;
        }
    }
    return color;
}

// Hook returning a mapper that adjusts an accent color for the active theme.
export function useAccentFn() {
    const { theme } = useTheme();
    return (color) => (theme === "light" ? accentForLight(color) : color);
}

// Apply an alpha channel to a color that may be a hex ("#RRGGBB"),
// an rgb()/hsl() functional value, or a CSS-variable form like
// "rgb(var(--tc-xxxxxx))". Accepts alpha as a 2-char hex string
// ("66") or a 0..1 number.
export function withAlpha(color, alpha) {
    if (color == null) return color;
    const s = String(color).trim();
    const a =
        typeof alpha === "string" && /^[0-9a-fA-F]{2}$/.test(alpha)
            ? parseInt(alpha, 16) / 255
            : Number(alpha);
    if (s.startsWith("rgb(") || s.startsWith("rgba(") || s.startsWith("hsl(") || s.startsWith("hsla(")) {
        const i = s.lastIndexOf(")");
        if (i === -1) return s;
        return s.slice(0, i) + ` / ${a})` + s.slice(i + 1);
    }
    if (s.startsWith("#") && (s.length === 7 || s.length === 4)) {
        const hex = Math.round(a * 255).toString(16).padStart(2, "0");
        return s + hex;
    }
    return s;
}
